import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadPurchaseByIdUrl,
  zadPurchaseCancelUrl,
  zadPurchaseCancellationPreviewUrl,
  zadPurchaseForceCancelUrl,
  zadPurchaseReconciliationSummaryUrl,
  zadPurchaseReconciliationUrl,
  zadPurchasesImportUrl,
  zadPurchasesUrl,
  zadPurchaseUpdateUrl,
  zadPurchaseValidateUrl,
  zadProductsUrl,
  zadSuppliersUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadPurchaseImportPayload} from '../zad/purchaseImportParse'
import {sanitizeZadPurchaseWritePayload} from '../zad/purchaseHelpers'
import {fetchZadStock} from '../zadStock/service'
import {
  CreateZadPurchaseRequest,
  FetchZadPurchaseByIdRequest,
  FetchZadPurchasePickerProductsRequest,
  FetchZadPurchasePickerSuppliersRequest,
  FetchZadPurchasesRequest,
  ExecuteZadPurchaseForceCancelRequest,
  ExecuteZadValidatedPurchaseCancelRequest,
  FetchZadPurchaseReconciliationRequest,
  ImportZadPurchaseRequest,
  UpdateZadPurchaseRequest,
  ZadPurchaseIdRequest,
} from './types'

export const fetchZadPurchases = createAsyncThunk(
  'zadPurchases/fetch',
  async (payload: FetchZadPurchasesRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadPurchasesUrl}?${buildZadListQuery(payload)}`)
      const {purchases, total, totalPages} = res.data || {}
      return {
        success: true as const,
        purchases: Array.isArray(purchases) ? purchases : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPurchaseById = createAsyncThunk(
  'zadPurchases/fetchById',
  async (payload: FetchZadPurchaseByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadPurchaseByIdUrl(payload.id))
      return {
        success: true as const,
        purchase: res.data.purchase,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadPurchase = createAsyncThunk(
  'zadPurchases/create',
  async (payload: CreateZadPurchaseRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadPurchaseWritePayload(payload)
      const res = await axios.post(zadPurchasesUrl, body)
      return {
        success: true as const,
        purchase: res.data.purchase,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadPurchase = createAsyncThunk(
  'zadPurchases/update',
  async (payload: UpdateZadPurchaseRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = sanitizeZadPurchaseWritePayload(values)
      const res = await axios.post(zadPurchaseUpdateUrl(id), body)
      return {
        success: true as const,
        purchase: res.data.purchase,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const importZadPurchase = createAsyncThunk(
  'zadPurchases/import',
  async (payload: ImportZadPurchaseRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadPurchaseImportPayload(payload)
      const res = await axios.post(zadPurchasesImportUrl, body)
      return {
        success: true as const,
        purchase: res.data.purchase,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const validateZadPurchase = createAsyncThunk(
  'zadPurchases/validate',
  async (payload: ZadPurchaseIdRequest, {rejectWithValue, dispatch}) => {
    try {
      const res = await axios.post(zadPurchaseValidateUrl(payload.id))
      dispatch(
        fetchZadStock({
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })
      )
      return {
        success: true as const,
        purchase: res.data.purchase,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadPurchase = createAsyncThunk(
  'zadPurchases/cancel',
  async (payload: ZadPurchaseIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadPurchaseCancelUrl(payload.id))
      return {
        success: true as const,
        purchase: res.data.purchase,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const previewZadPurchaseCancellation = createAsyncThunk(
  'zadPurchases/previewCancellation',
  async (payload: ZadPurchaseIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadPurchaseCancellationPreviewUrl(payload.id), {})
      return {
        success: true as const,
        preview: res.data,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const executeZadValidatedPurchaseCancel = createAsyncThunk(
  'zadPurchases/executeValidatedCancel',
  async (payload: ExecuteZadValidatedPurchaseCancelRequest, {rejectWithValue, dispatch}) => {
    try {
      const res = await axios.post(zadPurchaseCancelUrl(payload.id), {
        reason: payload.reason,
        archiveEligibleProducts: payload.archiveEligibleProducts,
        operationId: payload.operationId,
      })
      dispatch(
        fetchZadStock({
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })
      )
      return {
        success: true as const,
        purchase: res.data.purchase,
        cancellation: res.data.cancellation,
        stockReversal: res.data.stockReversal,
        productsToArchive: res.data.productsToArchive,
        productsToKeep: res.data.productsToKeep,
        archivedProductIds: res.data.archivedProductIds,
        reused: res.data.reused,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const executeZadPurchaseForceCancel = createAsyncThunk(
  'zadPurchases/executeForceCancel',
  async (payload: ExecuteZadPurchaseForceCancelRequest, {rejectWithValue, dispatch}) => {
    try {
      const body: Record<string, unknown> = {
        reason: payload.reason,
        operationId: payload.operationId,
        confirmForce: true,
        archiveEligibleProducts: payload.archiveEligibleProducts,
      }
      if (payload.confirmUnverifiedStock === true) {
        body.confirmUnverifiedStock = true
      }
      const res = await axios.post(zadPurchaseForceCancelUrl(payload.id), body)
      dispatch(
        fetchZadStock({
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })
      )
      return {
        success: true as const,
        purchase: res.data.purchase,
        cancellation: res.data.cancellation,
        stockReversal: res.data.stockReversal,
        productsToArchive: res.data.productsToArchive,
        productsToKeep: res.data.productsToKeep,
        archivedProductIds: res.data.archivedProductIds,
        reused: res.data.reused,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPurchaseReconciliationSummary = createAsyncThunk(
  'zadPurchases/fetchReconciliationSummary',
  async (_payload: void, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadPurchaseReconciliationSummaryUrl)
      return {
        success: true as const,
        payload: res.data,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPurchaseReconciliation = createAsyncThunk(
  'zadPurchases/fetchReconciliation',
  async (payload: FetchZadPurchaseReconciliationRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadPurchaseReconciliationUrl(payload.id))
      return {
        success: true as const,
        payload: res.data,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPurchasePickerSuppliers = createAsyncThunk(
  'zadPurchases/fetchPickerSuppliers',
  async (payload: FetchZadPurchasePickerSuppliersRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadSuppliersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {suppliers} = res.data || {}
      return {
        success: true as const,
        suppliers: Array.isArray(suppliers) ? suppliers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPurchasePickerProducts = createAsyncThunk(
  'zadPurchases/fetchPickerProducts',
  async (payload: FetchZadPurchasePickerProductsRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadProductsUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 50,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
          archiveStatus: 'ACTIVE',
          search: payload?.search || '',
        })}`
      )
      const {products} = res.data || {}
      return {
        success: true as const,
        products: Array.isArray(products) ? products : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
